import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { formatPrice, FREE_SHIPPING_THRESHOLD } from "@/data/products";
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight, Ticket, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CartDrawer = () => {
  const { cartLines, subtotal, setQty, removeItem, drawerOpen, setDrawerOpen, promo, setPromo, discount } = useCart();
  const navigate = useNavigate();
  const [promoInput, setPromoInput] = useState("");
  const [promoLoading, setPromoLoading] = useState(false);

  const applyPromo = async () => {
    if (!promoInput.trim()) return;
    setPromoLoading(true);
    try {
      const res = await axios.post(`${API}/promo/validate`, {
        code: promoInput.trim(),
        subtotal_cents: Math.round(subtotal * 100),
      });
      setPromo({ code: res.data.code, percent: res.data.percent });
      toast.success(`Code ${res.data.code} appliqué ✦`);
    } catch {
      toast.error("Code promo invalide ou expiré");
    } finally {
      setPromoLoading(false);
    }
  };
  const progress = Math.min(100, (subtotal / FREE_SHIPPING_THRESHOLD) * 100);
  const remaining = Math.max(0, FREE_SHIPPING_THRESHOLD - subtotal);

  return (
    <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
      <SheetContent data-testid="cart-drawer" className="w-full sm:max-w-md bg-cream border-l border-blush-deep flex flex-col p-0">
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-blush-deep">
          <SheetTitle className="font-serif text-2xl font-medium text-onyx flex items-center gap-2">
            <ShoppingBag size={20} className="text-terracotta" /> Votre Panier
          </SheetTitle>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {cartLines.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center gap-4" data-testid="cart-empty-state">
              <div className="w-16 h-16 rounded-full bg-blush flex items-center justify-center">
                <ShoppingBag size={24} className="text-rose-gold" />
              </div>
              <p className="font-serif text-xl text-onyx">Votre panier est encore vide</p>
              <p className="text-sm text-muted-warm">Offrez à votre peau son premier moment Relax.</p>
              <button
                data-testid="cart-empty-discover-button"
                onClick={() => { setDrawerOpen(false); navigate("/produit"); }}
                className="mt-2 rounded-full bg-onyx text-cream px-6 py-3 text-sm font-medium hover:bg-terracotta transition-colors"
              >
                Découvrir le masque
              </button>
            </div>
          ) : (
            <div className="space-y-5">
              {cartLines.map(({ variant, qty }) => (
                <div key={variant.id} data-testid={`cart-item-${variant.id}`} className="flex gap-4 bg-white rounded-2xl p-4 border border-blush-deep">
                  <img src={variant.image} alt={variant.label} className="w-16 h-20 object-cover rounded-xl" />
                  <div className="flex-1">
                    <p className="font-medium text-sm text-onyx">{variant.label}</p>
                    <p className="text-xs text-muted-warm mt-0.5">{variant.productName}</p>
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex items-center gap-2 border border-blush-deep rounded-full px-2 py-1">
                        <button data-testid={`cart-item-${variant.id}-decrease`} onClick={() => setQty(variant.id, qty - 1)} className="text-onyx hover:text-terracotta p-0.5"><Minus size={14} /></button>
                        <span data-testid={`cart-item-${variant.id}-qty`} className="text-sm font-mono w-5 text-center">{qty}</span>
                        <button data-testid={`cart-item-${variant.id}-increase`} onClick={() => setQty(variant.id, qty + 1)} className="text-onyx hover:text-terracotta p-0.5"><Plus size={14} /></button>
                      </div>
                      <p data-testid={`cart-item-${variant.id}-price`} className="font-serif text-lg text-terracotta">{formatPrice(variant.price * qty)}</p>
                    </div>
                  </div>
                  <button data-testid={`cart-item-${variant.id}-remove`} onClick={() => removeItem(variant.id)} className="self-start text-stone-400 hover:text-red-500 transition-colors"><Trash2 size={16} /></button>
                </div>
              ))}
            </div>
          )}
        </div>
        {cartLines.length > 0 && (
          <div className="border-t border-blush-deep px-6 py-5 bg-white/60">
            <div className="mb-4">
              <p data-testid="cart-shipping-progress-text" className="text-xs text-muted-warm mb-2">
                {remaining > 0 ? (
                  <>Plus que <span className="font-semibold text-terracotta">{formatPrice(remaining)}</span> pour la livraison offerte ✦</>
                ) : (
                  <span className="font-semibold text-[#2E6B4F]">Livraison offerte débloquée ✦</span>
                )}
              </p>
              <div className="h-1.5 rounded-full bg-blush overflow-hidden">
                <div data-testid="cart-shipping-progress-bar" className="h-full bg-rose-gold rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
              </div>
            </div>
            <div className="mb-4">
              {promo ? (
                <div data-testid="cart-promo-applied" className="flex items-center justify-between rounded-xl bg-[#2E6B4F]/5 border border-[#2E6B4F]/25 px-4 py-2.5">
                  <span className="text-xs font-mono font-bold text-[#2E6B4F] flex items-center gap-1.5"><Ticket size={13} /> {promo.code} · -{promo.percent}%</span>
                  <button data-testid="cart-promo-remove" onClick={() => setPromo(null)} className="text-stone-400 hover:text-red-500 transition-colors"><X size={14} /></button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input
                    data-testid="cart-promo-input"
                    type="text"
                    value={promoInput}
                    onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                    placeholder="Code promo (ex. BIENVENUE10)"
                    className="input-elegant flex-1 !py-2.5 text-xs"
                  />
                  <button
                    data-testid="cart-promo-apply"
                    onClick={applyPromo}
                    disabled={promoLoading}
                    className="rounded-xl bg-onyx text-cream px-4 text-xs font-semibold hover:bg-terracotta transition-colors disabled:opacity-60"
                  >
                    {promoLoading ? "…" : "Appliquer"}
                  </button>
                </div>
              )}
            </div>
            <div className="flex items-center justify-between mb-1">
              <p className="text-sm text-muted-warm">Sous-total</p>
              <p data-testid="cart-subtotal" className="font-serif text-2xl text-onyx">{formatPrice(subtotal)}</p>
            </div>
            {discount > 0 && (
              <div className="flex items-center justify-between mb-4">
                <p className="text-xs text-[#2E6B4F]">Code {promo.code} (-{promo.percent}%)</p>
                <p data-testid="cart-discount" className="text-sm font-semibold text-[#2E6B4F]">− {formatPrice(discount)}</p>
              </div>
            )}
            <button
              data-testid="cart-checkout-button"
              onClick={() => { setDrawerOpen(false); navigate("/checkout"); }}
              className="w-full rounded-full bg-terracotta text-cream py-4 text-sm font-semibold tracking-wide hover:bg-onyx transition-colors flex items-center justify-center gap-2"
            >
              Passer commande <ArrowRight size={16} />
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
